import { api } from './api';
import { TenantCreate, TenantUpdate, TenantConfig, DomainConfig } from '../types/tenant';

const superadminService = {
  // Tenant Management
  getTenants: async (skip = 0, limit = 100) => {
    const response = await api.get(`/superadmin/tenants?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  getTenant: async (tenantId: string) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}`);
    return response.data;
  },

  createTenant: async (tenantData: TenantCreate, domainConfig: DomainConfig) => {
    const response = await api.post('/superadmin/tenants', tenantData, {
      params: domainConfig
    });
    return response.data;
  },

  updateTenant: async (tenantId: string, tenantData: TenantUpdate) => {
    const response = await api.put(`/superadmin/tenants/${tenantId}`, tenantData);
    return response.data;
  },

  deleteTenant: async (tenantId: string) => {
    const response = await api.delete(`/superadmin/tenants/${tenantId}`);
    return response.data;
  },

  activateTenant: async (tenantId: string) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/activate`);
    return response.data;
  },

  // Domain Management
  updateTenantDomain: async (tenantId: string, domainConfig: DomainConfig) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/update-domain`, domainConfig);
    return response.data;
  },

  // Configuration Management
  getTenantConfig: async (tenantId: string) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/config`);
    return response.data;
  },

  updateTenantConfig: async (tenantId: string, config: TenantConfig) => {
    const response = await api.put(`/superadmin/tenants/${tenantId}/config`, config);
    return response.data;
  },

  testSmtpConfig: async (tenantId: string, testEmail: string) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/test-smtp`, null, {
      params: { test_email: testEmail }
    });
    return response.data;
  },

  // Domain Management
  getTenantDomains: async (tenantId: string) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/domains`);
    return response.data;
  },

  addTenantDomain: async (tenantId: string, domain: string, domainType: string = 'PRIMARY') => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/domains`, {
      domain,
      domain_type: domainType
    });
    return response.data;
  },

  removeTenantDomain: async (tenantId: string, domainId: string) => {
    const response = await api.delete(`/superadmin/tenants/${tenantId}/domains/${domainId}`);
    return response.data;
  },

  // Feature Management
  getTenantFeatures: async (tenantId: string) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/features`);
    return response.data;
  },

  updateTenantFeature: async (tenantId: string, featureKey: string, enabled: boolean, config?: any) => {
    const response = await api.put(`/superadmin/tenants/${tenantId}/features/${featureKey}`, {
      enabled,
      config
    });
    return response.data;
  },

  // Audit Log
  getTenantAuditLog: async (tenantId: string, limit = 100) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/audit-log?limit=${limit}`);
    return response.data;
  },

  // System Overview
  getSystemStats: async () => {
    const response = await api.get('/superadmin/system/stats');
    return response.data;
  },

  // User Management
  getAllUsers: async (skip = 0, limit = 100, tenantId?: string) => {
    const params = tenantId ? `?skip=${skip}&limit=${limit}&tenant_id=${tenantId}` : `?skip=${skip}&limit=${limit}`;
    const response = await api.get(`/superadmin/users${params}`);
    return response.data;
  },

  getTenantUsers: async (tenantId: string, skip = 0, limit = 100) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/users?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  addUserToTenant: async (tenantId: string, userData: {
    email: string;
    password?: string;
    full_name?: string;
    role: string;
    is_default?: boolean;
  }) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/users`, userData);
    return response.data;
  },

  updateUserInTenant: async (userId: number, tenantId: string, updateData: {
    role?: string;
    is_active?: boolean;
    is_default?: boolean;
  }) => {
    const response = await api.put(`/superadmin/users/${userId}/tenants/${tenantId}`, updateData);
    return response.data;
  },

  removeUserFromTenant: async (userId: number, tenantId: string) => {
    const response = await api.delete(`/superadmin/users/${userId}/tenants/${tenantId}`);
    return response.data;
  },

  updateUser: async (userId: number, userData: {
    email?: string;
    full_name?: string;
    password?: string;
    is_active?: boolean;
    is_superuser?: boolean;
  }) => {
    const response = await api.put(`/superadmin/users/${userId}`, userData);
    return response.data;
  },

  deleteUser: async (userId: number) => {
    const response = await api.delete(`/superadmin/users/${userId}`);
    return response.data;
  },

  getUserTenants: async (userId: number) => {
    const response = await api.get(`/superadmin/users/${userId}/tenants`);
    return response.data;
  },

  addUserToMultipleTenants: async (userId: number, tenantIds: string[], role: string) => {
    const response = await api.post(`/superadmin/users/${userId}/add-to-multiple-tenants`, {
      tenant_ids: tenantIds,
      role
    });
    return response.data;
  }
};

export default superadminService;